import React, { Component, Fragment } from 'react';
import Wahanda from 'common/wahanda';
import ROLES from 'common/constants/userRoles';
import { trackEvent } from 'common/analytics';
import { Notice } from 'components/common/Notice';
import { PERMISSIONS } from 'common/constants/userPermissions';
import {
  DisableReason,
  RolePermissions,
  canGrantFullPresetRole,
  resolveAllowedRoleAndPermissions,
} from 'components/employees/employeeEditPolicy';
import EmployeeRolesList from './EmployeeRolesList';
import style from './style.scss';

const lang = Wahanda.lang.settings.employees.employee;
// @ts-expect-error ts-migrate(2430) FIXME: Type 'number' is not assignable to type 'string | ... Remove this comment to see the full error message
interface IEmployeePermissionsProps extends React.HTMLAttributes<Element> {
  id?: number;
  role?: any;
  isLoaded?: boolean;
  showTaxAuditorRole?: boolean;
  showBasicRole?: boolean;
  pointOfSalePermissionsVisible?: boolean;
  selectedPermissions?: string[];
  permissionsByRoles?: {
    permissions?: string[];
    role?: any;
  }[];
  showAccessAttention?: boolean;
  actorPermissions?: string[];
  actorRoleCode?: string | null;
  masqueradingAsSupplier?: boolean;
  rolePermissionsDisabled?: boolean;
  rolePermissionsDisableReason?: DisableReason;
  actions: {
    setEmployeeDataAction: (...args: any[]) => any;
    requestPermissionsByRolesAction: (...args: any[]) => any;
  };
  setEmployeeDataAction?: any;
  requestPermissionsByRolesAction?: any;
}

export default class EmployeePermissions extends Component<
  IEmployeePermissionsProps,
  { createDefaultCorrected: boolean }
> {
  static defaultProps = {
    id: null,
    role: null,
    isLoaded: false,
    selectedPermissions: [],
    showTaxAuditorRole: false,
    showBasicRole: false,
    pointOfSalePermissionsVisible: false,
    permissionsByRoles: [],
    actorPermissions: [],
    actorRoleCode: null,
    masqueradingAsSupplier: false,
    rolePermissionsDisabled: false,
    showAccessAttention: false,
  };

  state = {
    createDefaultCorrected: false,
  };

  componentDidMount() {
    this.props.actions.requestPermissionsByRolesAction();
    this.correctDefaultRole();
  }

  componentDidUpdate() {
    this.correctDefaultRole();
  }

  correctDefaultRole = () => {
    const { id, role, permissionsByRoles = [], actorPermissions = [] } = this.props;

    if (permissionsByRoles.length === 0) {
      return;
    }

    const lacksEditUserPermissions = !actorPermissions.includes(PERMISSIONS.editUserPermissions);

    if (lacksEditUserPermissions) {
      if (id == null && role !== ROLES.BASIC) {
        this.props.actions.setEmployeeDataAction({ role: ROLES.BASIC, permissions: [] });
      }
      return;
    }

    if (this.state.createDefaultCorrected || id != null) {
      return;
    }

    const canGrantTherapistPreset = canGrantFullPresetRole(
      actorPermissions,
      ROLES.THERAPIST,
      permissionsByRoles as RolePermissions[],
    );

    if (canGrantTherapistPreset) {
      if (role == null) {
        this.props.actions.setEmployeeDataAction({ role: ROLES.THERAPIST });
      }
    } else {
      this.props.actions.setEmployeeDataAction({ role: ROLES.USER, permissions: [] });
    }

    this.setState({ createDefaultCorrected: true });
  };

  onPermissionsSelect = (permissions) => {
    if (this.props.rolePermissionsDisabled) {
      return;
    }

    const {
      permissionsByRoles = [],
      id,
      selectedPermissions = [],
      actorPermissions = [],
      role: currentRole,
    } = this.props;

    const resolved = resolveAllowedRoleAndPermissions(
      actorPermissions,
      ROLES.USER,
      permissions,
      permissionsByRoles as RolePermissions[],
      {
        isCreate: id == null,
        currentTarget:
          id != null ? { role: currentRole ?? null, permissions: selectedPermissions } : undefined,
      },
    );

    this.setRole(ROLES.USER);

    trackEvent('team-member-permission', 'submit', 'update-permission');
    if (selectedPermissions.some((permission) => !resolved.permissions.includes(permission))) {
      trackEvent('connect-employee', 'permissions-updated', id);
    }
    this.props.actions.setEmployeeDataAction({ permissions: resolved.permissions });
  };

  onRoleSelect = (role) => {
    if (this.props.rolePermissionsDisabled) {
      return;
    }

    const {
      actorPermissions = [],
      permissionsByRoles = [],
      selectedPermissions = [],
      id,
      role: currentRole,
    } = this.props;

    if (role === ROLES.USER) {
      this.setRole(role);
      return;
    }

    const resolved = resolveAllowedRoleAndPermissions(
      actorPermissions,
      role,
      [],
      permissionsByRoles as RolePermissions[],
      {
        isCreate: id == null,
        currentTarget:
          id != null ? { role: currentRole ?? null, permissions: selectedPermissions } : undefined,
      },
    );

    this.props.actions.setEmployeeDataAction({
      role: resolved.role,
      permissions: resolved.permissions,
    });

    if (resolved.role === ROLES.TAX_AUDITOR) {
      this.props.actions.setEmployeeDataAction({ takesAppointments: false });
    }
  };

  setRole = (role) => {
    this.props.actions.setEmployeeDataAction({ role });
    if (role === ROLES.TAX_AUDITOR) {
      this.props.actions.setEmployeeDataAction({ takesAppointments: false });
    }
  };

  render() {
    const {
      showAccessAttention,
      role,
      showTaxAuditorRole,
      showBasicRole,
      pointOfSalePermissionsVisible,
      selectedPermissions,
      permissionsByRoles,
      rolePermissionsDisabled,
      rolePermissionsDisableReason,
      actorPermissions,
      actorRoleCode,
      masqueradingAsSupplier,
    } = this.props;
    return (
      <Fragment>
        <div className={style.header}>{lang.permissions.header}</div>
        {showAccessAttention && <Notice type="info" message={lang.connectAccessAttention} />}
        <EmployeeRolesList
          selectedRole={role}
          // @ts-expect-error ts-migrate(2769) FIXME: Type 'undefined' is not assignable to type '{ perm... Remove this comment to see the full error message
          permissionsByRoles={permissionsByRoles}
          showTaxAuditorRole={showTaxAuditorRole}
          showBasicRole={showBasicRole}
          pointOfSalePermissionsVisible={pointOfSalePermissionsVisible}
          selectedPermissions={selectedPermissions}
          onPermissionsSelect={this.onPermissionsSelect}
          onRoleSelect={this.onRoleSelect}
          disabled={rolePermissionsDisabled}
          disableReason={rolePermissionsDisableReason}
          actorPermissions={actorPermissions}
          actorRoleCode={actorRoleCode}
          masqueradingAsSupplier={masqueradingAsSupplier}
        />
      </Fragment>
    );
  }
}
